var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, cloneElement, useContext, useMemo } from 'react';
import CBForm from '@cb/apricot/CBForm';
import cn from 'classnames';
import { flatMap } from 'lodash';
import { Spacer, SpacerH } from '@cb/apricot-react-spacer';
import { useUID } from 'react-uid';
import { isElement, isReactComponent } from '@cb/react-utils';
const intersperse = (arr, inter) => flatMap(arr, (a, i) => (i ? [inter, a] : [a]));
const CheckboxContext = React.createContext({
    groupLabelPosition: '',
});
export const CheckboxGroup = ({ fieldsetId, validationMessageId, ariaDescribedby, legend, legendClassName, legendId, children, required, labelPosition = 'right', vertical = true, vSpacing = '16', hSpacing = '16', hasGrid = false, validation, validationMsg, a11yValidation = false, helperClassName, ariaLive, validationMsgRole = 'alert', role = 'group', }) => {
    const uid = useUID();
    // if legendId is not provided, generate one for the internal legend otherwise use what's given
    // eslint-disable-next-line no-param-reassign
    legendId = legend ? legendId || `apricot_legend_${uid}` : legendId;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_check_group_err_${uid}`;
    const spacedChildren = intersperse(children, vertical ? React.createElement(Spacer, { size: vSpacing }) : React.createElement(SpacerH, { size: hSpacing }));
    const withKey = spacedChildren
        .filter(child => isElement(child) || isReactComponent(child))
        .map((child, i) => {
        return cloneElement(child, { key: `key_${uid}${i}` });
    });
    const legendClass = cn('cb-label', validation === 'error' && `cb-validation-${validation}`, required && 'cb-required', legendClassName);
    const helperClass = cn('cb-input-helper', validation === 'error' && `cb-validation-${validation}`, helperClassName);
    const offScreenMsg = [];
    required && offScreenMsg.push('required');
    validation === 'error' && offScreenMsg.push('invalid');
    const acceptedValidation = validation === 'error' || validation === 'general';
    const getDescribedBy = () => {
        if (validationMsg && acceptedValidation && ariaDescribedby) {
            return `${ariaDescribedby} ${validationMessageId}`;
        }
        return validationMsg && acceptedValidation ? validationMessageId : ariaDescribedby;
    };
    const describedbyValue = (acceptedValidation || ariaDescribedby) && getDescribedBy();
    const context = useMemo(() => ({ groupLabelPosition: vertical && labelPosition === 'left' ? 'left' : 'right' }), [vertical, labelPosition]);
    return (React.createElement("fieldset", { id: fieldsetId, "aria-labelledby": legendId, className: cn(validation === 'error' && `cb-validation-${validation}`), "aria-describedby": describedbyValue || undefined, role: role || undefined },
        legend && (React.createElement(React.Fragment, null,
            React.createElement("legend", { className: legendClass, id: legendId },
                legend,
                React.createElement("span", { className: "sr-only" }, offScreenMsg.join(' - '))),
            React.createElement(Spacer, { size: "8" }))),
        React.createElement("div", { className: cn(!vertical && 'display-flex flex-row', vertical && labelPosition === 'left' && 'cb-width-100', hasGrid && 'cb-width-100') },
            React.createElement(CheckboxContext.Provider, { value: context }, hasGrid ? children : withKey)),
        ((validation === 'error' && validationMsg) || a11yValidation) && (React.createElement("p", { id: validationMessageId, className: cn(helperClass, a11yValidation && 'cb-no-margin'), "aria-live": ariaLive, role: validationMsgRole }, !a11yValidation && validationMsg))));
};
export const Checkbox = (_a) => {
    var { validationMessageId, ariaDescribedby, label, labelId, ariaLabel, labelClassName, required, labelPosition: checkboxLabelPosition = 'right', disabled, condensed = false, checked, defaultChecked, id, name, className, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, onChange } = _a, props = __rest(_a, ["validationMessageId", "ariaDescribedby", "label", "labelId", "ariaLabel", "labelClassName", "required", "labelPosition", "disabled", "condensed", "checked", "defaultChecked", "id", "name", "className", "validation", "validationMsg", "validationMsgRole", "helperClassName", "ariaLive", "onChange"]);
    const { groupLabelPosition } = useContext(CheckboxContext);
    const labelPosition = checkboxLabelPosition || groupLabelPosition;
    const ref = useCallback((node) => {
        if (node) {
            CBForm.customFormElement({
                elem: node,
                markup: false,
                type: 'checkbox',
                onKeydownReact: true,
            });
        }
    }, []);
    const cName = cn('cb-checkbox', className, disabled && 'cb-disabled', condensed && 'cb-condensed', labelPosition === 'left' && 'cb-label-left', validation === 'error' && `cb-validation-error`);
    const helperClass = cn('cb-input-helper', validation === 'error' && `cb-validation-${validation}`, helperClassName);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    id = id || `apricot_check_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_check_err_${uid}`;
    const acceptedValidation = validation === 'error' || validation === 'general';
    const getDescribedBy = () => {
        if (validationMsg && acceptedValidation && ariaDescribedby) {
            return `${ariaDescribedby} ${validationMessageId}`;
        }
        return validationMsg && acceptedValidation ? validationMessageId : ariaDescribedby;
    };
    const describedbyValue = (acceptedValidation || ariaDescribedby) && getDescribedBy();
    /** Enter key handler */
    const handleKeyDown = (e) => {
        // if it's enter ket
        if ((e.keyCode ? e.keyCode : e.which) === 13) {
            e.target.checked = !e.target.checked;
            onChange && onChange(e.target.checked, e);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: cName, "data-cbtrack-comp": "apricot-react:checkbox" },
            React.createElement("label", { id: labelId, className: cn(required && 'cb-required', labelClassName), htmlFor: id },
                labelPosition === 'left' && label,
                React.createElement("input", Object.assign({ id: id, name: name, ref: ref, type: "checkbox", checked: checked, defaultChecked: defaultChecked, disabled: disabled, onKeyDown: e => handleKeyDown(e), onChange: e => onChange && onChange(e.target.checked, e), "aria-label": ariaLabel, "aria-invalid": validation === 'error', "aria-describedby": describedbyValue || undefined }, props)),
                labelPosition !== 'left' && label)),
        (validation === 'error' || validation === 'general') && validationMsg && (React.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
};
//# sourceMappingURL=Checkbox.js.map